import React, { useEffect, useMemo, useRef, useState } from "react";
import { BaseModal, IModalInstance, ModalHeader } from "@whatslly/ui-components/modals";
import { useTranslation } from "react-i18next";
import { Field, FieldProps, FormikErrors, FormikProps } from "formik";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import styles from "./ContactDetailsModal.module.scss";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import { PhoneInput } from "@whatslly/ui-components/v2/BaseElements/PhoneInput";
import { countryCodes } from "@whatslly/commons/phoneUtils/fullCountryCodesData";
import { getPhoneCountryCode } from "@whatslly/commons/utils";
import { SelectedCountriesType } from "@whatslly/ui-components/v2/BaseElements/CountryCodeSelect";
import { COUNTRIES } from "@whatslly/commons/phoneUtils/countries";

const empty: any[] = [];

const BASE_DATA = {
  firstName: "",
  lastName: "",
  phone: "",
};
type FormDataType = typeof BASE_DATA;
export const ContactDetailsModal: React.FC<IModalInstance<FormDataType> & { initData?: FormDataType; hidePhone?: boolean; title: string }> = ({
  closeModal,
  initData,
  title,
  hidePhone,
}) => {
  const [countryCode, setCountryCode] = useState("");
  const [phoneNumberWithOutCountryCode, setPhoneNumberWithOutCountryCode] = useState("");
  const textInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const formikContext = useRef<FormikProps<FormDataType>>(null);
  const [formikErrorHasErrors, setFormikErrorHasErrors] = useState(true);
  const [selectedCountryCodes, setSelectedCountryCodes] = useState<SelectedCountriesType>(empty);

  const validate = (values: FormDataType) => {
    const errors: FormikErrors<PartialRecord<keyof FormDataType, string>> = {};
    if (!values.firstName) errors.firstName = t("field_is_required");
    if (!values.lastName) errors.lastName = t("field_is_required");
    if (!values.phone && !hidePhone) errors.phone = t("field_is_required");

    Object.keys(errors).length > 0 ? setFormikErrorHasErrors(true) : setFormikErrorHasErrors(false);
    return errors;
  };

  const handleCountryCodeChange = (code: string, fieldData: FieldProps<string, typeof BASE_DATA>) => {
    setCountryCode(code);
    fieldData.form.setFieldValue(fieldData.field.name, code + phoneNumberWithOutCountryCode);
    textInputRef.current?.focus();
  };

  const handlePhoneChange = (phone: string, fieldData: FieldProps<string, typeof BASE_DATA>) => {
    setPhoneNumberWithOutCountryCode(phone);
    fieldData.form.setFieldValue(fieldData.field.name, countryCode + phone);
  };

  const isValidPhone = useMemo(() => {
    if (hidePhone) {
      return true;
    }
    const thisCountryCodeAsNumber = Number(countryCode);
    const currentCountryData = countryCodes.filter((c) => c.code === thisCountryCodeAsNumber);
    const phone = `${countryCode}${phoneNumberWithOutCountryCode}`;
    const phoneFitToRegex = currentCountryData.find((c) => new RegExp(c.regex).test(phone));
    return phoneNumberWithOutCountryCode.length >= 8 && phoneNumberWithOutCountryCode.length <= 15 && Boolean(phoneFitToRegex);
  }, [countryCode, hidePhone, phoneNumberWithOutCountryCode]);

  useEffect(() => {
    if (!initData) return;
    const phoneNumber = initData.phone;
    const cc = getPhoneCountryCode(phoneNumber);
    if (cc) {
      setCountryCode(cc);
      setPhoneNumberWithOutCountryCode(phoneNumber.replace(cc, ""));
      setSelectedCountryCodes([{ code: cc, country: COUNTRIES.find((data) => data.code === cc)?.country ?? "" }]);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BaseModal
      header={<ModalHeader closeModal={closeModal}>{title}</ModalHeader>}
      footer={
        <ModalButtons
          submitText={t("whatsapp_business_api_agent_page.save")}
          testId={`add-contact-ok-button`}
          onSubmit={() => formikContext.current?.submitForm()}
          hideCancel={true}
          isSubmitDisabled={!isValidPhone || formikErrorHasErrors}
        />
      }
      closeModal={closeModal}
      className={styles.modal}
      bodyClassName={styles.body}
      closeOnClickOutside={false}
      closeOnEscape={false}
      testId={`add_contact_modal`}
      headerClassName={styles.header}
      backdropClassName={styles.backdrop}
    >
      <FormElement initialValues={initData ?? BASE_DATA} validate={validate} onSubmit={(data) => closeModal(data)} formikRef={formikContext}>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("whatsapp_business_api_agent_page.first_name")} required />
          <FormTextInput name={`firstName`} paddingInline={`none`} />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("whatsapp_business_api_agent_page.last_name")} required />
          <FormTextInput name={`lastName`} paddingInline={`none`} />
        </div>
        {!hidePhone && (
          <Field
            name={`phone`}
            children={(fieldData: FieldProps<string, typeof BASE_DATA>) => {
              return (
                <div className={styles.field}>
                  <Label className={styles.filedLabel} text={t("whatsapp_business_api_agent_page.phone")} required />
                  <PhoneInput
                    testid="add_contact_phone_input"
                    disableBackgroundColor={true}
                    countryCode={countryCode}
                    phoneNumberWithOutCountryCode={phoneNumberWithOutCountryCode}
                    onPhoneChange={(phoneNumberWithOutCountryCode) => handlePhoneChange(phoneNumberWithOutCountryCode, fieldData)}
                    onCountryCodeChange={(code: string) => handleCountryCodeChange(code, fieldData)}
                    selectedCountryCodes={selectedCountryCodes}
                    mostUsedCountryCodes={empty}
                    disableCountryCodeAutoPick={false}
                  />
                </div>
              );
            }}
          />
        )}
      </FormElement>
    </BaseModal>
  );
};
